import React, { useState } from 'react';
import { Dialog, Box, Typography, Button, IconButton, Tooltip } from '@mui/material';
import CardGiftcardRoundedIcon from '@mui/icons-material/CardGiftcardRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { useUser } from '../../../../contexts/UserContext';
import { PT, dialogPaperSx, primaryBtnSx, kickerSx } from '../../../../components/portalUi';

type ReferralModalProps = {
  isOpen: boolean;
  handleClose: () => void;
};

export const ReferralModal: React.FC<ReferralModalProps> = ({ isOpen, handleClose }) => {
  const { user } = useUser();
  const [copied, setCopied] = useState(false);
  const code = user.referralCode || '—';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be blocked; the code is visible to copy manually */
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: dialogPaperSx }}
    >
      <Box sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            mx: 'auto',
            mb: 2,
            borderRadius: '18px',
            display: 'grid',
            placeItems: 'center',
            color: PT.teal,
            background: PT.tealSoft,
            border: `1px solid ${PT.tealBorder}`
          }}
        >
          <CardGiftcardRoundedIcon sx={{ fontSize: 32 }} />
        </Box>

        <Typography sx={{ ...kickerSx, mb: 1 }}>Referrals</Typography>
        <Typography sx={{ fontWeight: 800, fontSize: 22, color: PT.text, lineHeight: 1.2 }}>
          The more you refer, the more you earn
        </Typography>
        <Typography sx={{ mt: 1, color: PT.textMuted, fontSize: 14.5 }}>
          Earn{' '}
          <Box component="span" sx={{ color: PT.teal, fontWeight: 800 }}>
            10%
          </Box>{' '}
          of your referrals&rsquo; activity.
        </Typography>

        <Box
          sx={{
            mt: 3,
            p: 1.5,
            borderRadius: '12px',
            border: `1px solid ${PT.border}`,
            background: 'rgba(255,255,255,0.03)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
            <Typography
              sx={{
                font: '700 10px Montserrat, sans-serif',
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: PT.textMuted
              }}
            >
              Referral code
            </Typography>
            <Typography
              noWrap
              sx={{
                mt: 0.3,
                font: '800 20px ui-monospace, Menlo, Consolas, monospace',
                color: PT.tealText,
                letterSpacing: '0.06em'
              }}
            >
              {code}
            </Typography>
          </Box>
          <Tooltip title={copied ? 'Copied' : 'Copy'} placement="top" arrow>
            <IconButton
              onClick={copy}
              aria-label="Copy referral code"
              sx={{
                color: copied ? PT.green : PT.teal,
                border: `1px solid ${PT.border}`,
                borderRadius: '10px',
                '&:hover': { borderColor: PT.tealBorder, background: 'rgba(56,232,255,0.08)' }
              }}
            >
              {copied ? <CheckRoundedIcon /> : <ContentCopyRoundedIcon />}
            </IconButton>
          </Tooltip>
        </Box>

        <Button fullWidth onClick={handleClose} sx={{ ...primaryBtnSx, mt: 3, py: 1.1 }}>
          Close
        </Button>
      </Box>
    </Dialog>
  );
};
